import { useEffect, useState } from "react";
import { Box, Card, CardContent, Divider, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody, Chip, Skeleton, Alert, Stack, Pagination } from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { useProtectedRoute } from "../hooks/useProtectedRoute";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

type ActivityItem = {
  id?: string;
  refId?: string;
  type: "Ticket" | "Invoice" | "Payment" | "POS" | "Inventory" | "Client";
  ref: string;
  client: string;
  status: string;
  amount?: number | string;
  date: string;
};

export default function ActivityListPage() {
  useProtectedRoute();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fmtAmt = (v: unknown) => new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(Number(String(v ?? 0).replace(/[^\d.-]/g, "")) || 0);
  const fmtDateTime = (iso: string) => new Date(iso).toLocaleString();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.get("/activity?limit=50", { headers: { Authorization: `Bearer ${token}` } });
        setItems(res.data || []);
      } catch (e: any) {
        setError(e?.response?.data?.message || "Failed to load activity");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const linkFor = (r: ActivityItem) => {
    const id = r.refId || r.id || "";
    if (!id) return null;
    switch (r.type) {
      case "Ticket":
        return `/tickets/${id}`;
      case "Invoice":
        return `/invoices/${id}`;
      case "Payment":
      case "POS":
        return `/pos/sale/${id}`;
      case "Inventory":
        return r.ref?.startsWith?.("PO-") ? `/purchase-orders/${id}` : `/inventory/${id}`;
      case "Client":
        return `/clients/${id}/profile`;
      default:
        return null;
    }
  };

  return (
    <Box>
      <Card elevation={0} sx={{ borderRadius: 3, border: (theme) => `1px solid ${theme.palette.divider}` }}>
        <CardContent>
          <Typography variant="h5" fontWeight={900}>Recent Activity</Typography>
          <Divider sx={{ mt: 1.5, mb: 2 }} />

          {error && <Alert severity="error">{error}</Alert>}
          {loading ? (
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
              <Table size="small">
                <TableBody>
                  {Array.from({ length: 8 }).map((_, i) => (
                    <TableRow key={`skel-${i}`}>
                      <TableCell colSpan={6}><Skeleton height={28} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          ) : items.length === 0 ? (
            <Alert severity="info">No activity yet.</Alert>
          ) : (
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Ref</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">When</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((r, i) => (
                    <TableRow
                      key={`act-${i}`}
                      hover
                      onClick={() => { const link = linkFor(r); if (link) navigate(link); }}
                      sx={{ cursor: linkFor(r) ? 'pointer' : 'default' }}
                    >
                      <TableCell>
                        <Chip size="small" label={r.type} color={r.type === 'Invoice' ? 'success' : r.type === 'Payment' || r.type === 'POS' ? 'info' : 'secondary'} />
                      </TableCell>
                      <TableCell>{r.ref}</TableCell>
                      <TableCell>{r.client}</TableCell>
                      <TableCell>{r.status}</TableCell>
                      <TableCell align="right">{r.amount != null ? fmtAmt(r.amount) : '-'}</TableCell>
                      <TableCell align="right">{fmtDateTime(r.date)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}

          {/* Optional: pagination placeholder for future */}
          {!loading && items.length > 0 && (
            <Stack alignItems="flex-end" sx={{ mt: 2 }}>
              <Pagination count={1} page={1} size="small" />
            </Stack>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
